import React, { useState } from 'react';

type BadgeKey =
  | '21 CFR Part 11'
  | '21 CFR 312.32'
  | '21 CFR 312'
  | 'ICH E6(R3)'
  | 'ICH E6(R3) RBQM'
  | 'ICH E6(R3) Annex 1'
  | 'ALCOA'
  | 'CTCAE v5.0'
  | 'MHRA Apr 2026'
  | 'UK CTR 2025';

const BADGE_COLORS: Record<BadgeKey, string> = {
  '21 CFR Part 11': '#4299e1',
  '21 CFR 312.32': '#4299e1',
  '21 CFR 312': '#4299e1',
  'ICH E6(R3)': '#38b2ac',
  'ICH E6(R3) RBQM': '#38b2ac',
  'ICH E6(R3) Annex 1': '#38b2ac',
  'ALCOA': '#9f7aea',
  'CTCAE v5.0': '#9f7aea',
  'MHRA Apr 2026': '#48bb78',
  'UK CTR 2025': '#48bb78',
};

const BADGE_TOOLTIPS: Record<BadgeKey, string> = {
  '21 CFR Part 11':
    'FDA regulation requiring secure computer-generated time-stamped audit trails for all electronic records. D3RI\'s SHA-256 hash chain satisfies Section 11.10(e) at the decision-state level.',
  '21 CFR 312.32':
    'FDA regulation mandating SAE reporting windows \u2014 7 days for fatal/life-threatening, 15 days for all others. D3RI\'s SAE Clock enforces both windows automatically.',
  '21 CFR 312':
    'FDA regulation governing IND applications and Phase 1 trial conduct including adverse event reporting, protocol compliance, and sponsor monitoring obligations.',
  'ICH E6(R3)':
    'Good Clinical Practice guideline finalized January 2025, adopted by FDA September 2025, MHRA April 2026. Requires risk-based monitoring, ALCOA data governance, and validated audit systems. D3RI\'s bi-temporal graph satisfies Contemporaneous and Original requirements at decision-state level.',
  'ICH E6(R3) RBQM':
    'Risk-Based Quality Management \u2014 ICH E6(R3) central organizing principle requiring systematic identification and control of risks to data quality. D3RI\'s Site Risk Monitor scores each site and directs monitoring resources proportionately.',
  'ICH E6(R3) Annex 1':
    'ICH E6(R3) Annex 1 covers interventional clinical trials including investigational product management. D3RI\'s IP Accountability log satisfies the dispensing record and chain of custody requirements.',
  'ALCOA':
    'FDA/ICH data integrity standard requiring records to be Attributable, Legible, Contemporaneous, Original, and Accurate. D3RI\'s bi-temporal schema captures who recorded what, when it happened, and when it was recorded \u2014 satisfying all five ALCOA elements.',
  'CTCAE v5.0':
    'Common Terminology Criteria for Adverse Events version 5.0 \u2014 FDA standard for grading adverse event severity 1-5. D3RI\'s M1 Safety model applies CTCAE grading automatically to all AE reports.',
  'MHRA Apr 2026':
    'UK Medicines and Healthcare products Regulatory Agency \u2014 new clinical trial regulations effective April 28 2026 mandating ICH E6(R3), 25-year record retention, and digital audit trails. D3RI\'s immutable hash chain satisfies the 25-year tamper-evident record requirement.',
  'UK CTR 2025':
    'UK Clinical Trials (Amendment) Regulations 2025 \u2014 effective April 28 2026. Requires risk-based monitoring, digital records, and alignment with ICH E6(R3). Non-compliance carries criminal penalties and MHRA violation notices.',
};

interface ComplianceBadgesProps {
  badges: BadgeKey[];
}

function Badge({ label }: { label: BadgeKey }) {
  const [hover, setHover] = useState(false);
  const color = BADGE_COLORS[label] || '#a0aec0';
  const tooltip = BADGE_TOOLTIPS[label] || '';

  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span
        style={{
          display: 'inline-block',
          fontSize: 11,
          fontWeight: 600,
          color,
          border: `1px solid ${color}`,
          borderRadius: 12,
          padding: '2px 10px',
          whiteSpace: 'nowrap',
          cursor: 'default',
          lineHeight: '18px',
        }}
      >
        {label}
      </span>
      {hover && tooltip && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: 6,
            backgroundColor: '#1a202c',
            border: '1px solid #4a5568',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 11,
            lineHeight: 1.5,
            color: '#cbd5e0',
            width: 260,
            zIndex: 50,
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            pointerEvents: 'none',
          }}
        >
          {tooltip}
        </div>
      )}
    </span>
  );
}

export default function ComplianceBadges({ badges }: ComplianceBadgesProps) {
  const [infoHover, setInfoHover] = useState(false);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        margin: '8px 0 12px 0',
      }}
    >
      <span
        style={{ position: 'relative', display: 'inline-block', cursor: 'default' }}
        onMouseEnter={() => setInfoHover(true)}
        onMouseLeave={() => setInfoHover(false)}
      >
        <span style={{ fontSize: 14, color: '#718096' }}>{'\u24D8'}</span>
        {infoHover && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: 6,
              backgroundColor: '#1a202c',
              border: '1px solid #4a5568',
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 11,
              color: '#cbd5e0',
              whiteSpace: 'nowrap',
              zIndex: 50,
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              pointerEvents: 'none',
            }}
          >
            Regulatory standards satisfied by this view
          </div>
        )}
      </span>
      {badges.map((key) => (
        <Badge key={key} label={key} />
      ))}
    </div>
  );
}
